import React from 'react';
import PropTypes from 'prop-types';
import BrandWordsSection from 'components/internal/review/BrandWordsSection';
import Collapsible from 'components/molecules/Collapsible';
import { hasContent } from 'utilities/array';
import styles from './PostInfoSection.module.css';

export const propTypes = {
  content: PropTypes.node,
  header: PropTypes.node.isRequired,
  isInitiallyOpen: PropTypes.bool,
};

const defaultProps = {
  content: undefined,
  isInitiallyOpen: false,
};

function renderAudioTags(audioTranscription) {
  if (!audioTranscription) return null;

  return (
    <div className="u-flexColumn u-gap-8">
      <div className="t-overline">Language</div>
      <div>{ audioTranscription.language }</div>
      <div className="t-overline u-marginTop-8">Transcription</div>
      <div>{ audioTranscription.transcription }</div>
    </div>
  );
}

function renderBrandWords(brandWords, companyName, isBranded, isOmnibrand) {
  if (!isBranded || !hasContent(brandWords)) return null;

  return (
    <BrandWordsSection
      brandWords={brandWords}
      companyName={companyName}
      isOmnibrand={isOmnibrand}
    />
  );
}

function renderCreativeDetails(creativeDetails) {
  if (!hasContent(creativeDetails)) return null;

  return (
    <div className="u-flexColumn u-gap-16">
      { creativeDetails.map(({ label, value }) => (
        <div key={label}>
          <div className="t-overline u-marginBottom-8">{ label }</div>
          <div>{ value }</div>
        </div>
      )) }
    </div>
  );
}

function renderOcrTags(ocrTags) {
  if (!hasContent(ocrTags)) return null;

  return (
    <div className="u-flexColumn u-gap-16">
      { ocrTags.map(({ startTime, text }) => (
        <div
          key={`${text}::${startTime}`}
          className="u-flexRow u-justifyBetween u-gap-16"
        >
          <span>{ text }</span>
          <span>{ startTime }</span>
        </div>
      )) }
    </div>
  );
}

function renderPostCopy(postCopy) {
  if (!postCopy) return null;

  return (
    <div className="u-flexColumn u-gap-16">
      { postCopy.map(({ copy, version }) => (
        <div key={version ?? 1}>
          { version && (
            <div className="t-overline u-marginBottom-8">
              { `Version ${version}` }
            </div>
          ) }
          <div>{ copy }</div>
        </div>
      )) }
    </div>
  );
}

function renderTextScreenshots(content) {
  if (!content) return null;

  return (
    <div>
      <h6 className="u-marginBottom-16">
        { content.instructions }
      </h6>
      <div>
        { content.screenshots.map((screenshot) => (
          <div key={screenshot.screenshot} className="u-marginBottom-16">
            <img alt="Screenshot" src={screenshot.screenshot} />
            <span>{ ` ${screenshot.text}` }</span>
          </div>
        )) }
      </div>
    </div>
  );
}

function renderThumbnailImage(url) {
  if (!url) return null;

  return (
    <div className={styles.thumbnail}>
      <img alt="Thumbnail" src={url} />
    </div>
  );
}

function renderVideoShots(videoShots) {
  if (!hasContent(videoShots)) return null;

  return (
    <div className="u-flexColumn u-gap-24">
      { videoShots.map(({
        duration,
        endTime,
        startTime,
        url,
      }) => (
        <div key={`${startTime}-${endTime}`} className="u-flexRow u-gap-16">
          <div className={styles.image}>
            <img alt="Screenshot" src={url} />
          </div>
          <div className="u-flexColumn u-gap-16">
            <div>
              <div className="t-overline u-marginBottom-8">Time</div>
              <div>
                { `${startTime} - ${endTime}` }
              </div>
            </div>
            <div>
              <div className="t-overline u-marginBottom-8">Duration</div>
              <div>
                { duration }
                s
              </div>
            </div>
          </div>
        </div>
      )) }
    </div>
  );
}

export function getTopSections(postInfo) {
  const {
    postCopy,
    thumbnail,
  } = postInfo;

  return [
    {
      content: renderPostCopy(postCopy),
      header: 'Post Copy',
    },
    {
      content: renderThumbnailImage(thumbnail?.url),
      header: 'Thumbnail Image',
      isInitiallyOpen: true,
    },
    {
      content: renderOcrTags(thumbnail?.ocrTags),
      header: 'Thumbnail OCR',
    },
  ];
}

export function getSections(postInfo) {
  const {
    audioTranscription,
    brandWords,
    companyName,
    creativeDetails,
    isBranded,
    isOmnibrand,
    ocrTags,
    textScreenshotsSection,
    videoShots,
  } = postInfo;

  return [
    {
      content: renderBrandWords(brandWords, companyName, isBranded, isOmnibrand),
      header: 'Brand Words',
    },
    {
      content: renderOcrTags(ocrTags),
      header: 'OCR Tags',
    },
    {
      content: renderTextScreenshots(textScreenshotsSection?.contrast),
      header: 'Text Contrast',
    },
    {
      content: renderTextScreenshots(textScreenshotsSection?.height),
      header: 'Text Height',
      isInitiallyOpen: true,
    },
    {
      content: renderAudioTags(audioTranscription),
      header: 'Audio Tags',
    },
    {
      content: renderVideoShots(videoShots),
      header: `Video Shots (${videoShots?.length})`,
    },
    {
      content: renderCreativeDetails(creativeDetails),
      header: 'Creative Details',
    },
  ];
}

function PostInfoSection({
  header,
  isInitiallyOpen,
  content,
}) {
  if (!content) return null;

  return (
    <div className={styles.section}>
      <Collapsible
        header={<div className={styles.header}>{ header }</div>}
        isInitiallyOpen={isInitiallyOpen}
        useHeaderClick
      >
        <div className={`u-scrollShadowBottom ${styles.content}`}>
          { content }
        </div>
      </Collapsible>
    </div>
  );
}

PostInfoSection.propTypes = propTypes;
PostInfoSection.defaultProps = defaultProps;

export default PostInfoSection;
